import React from "react";
import styled from "styled-components";
import { StepContentStudio, StepFlow, STEP_FLOW_META } from "./StepContentStudio";
import AdsStudio from "./AdsStudio";
import {
  SectionRoot,
  SectionTitle,
  SectionSub,
  CardGrid,
} from "../shared";

// The Create hub: pick a format, then drop into that linear flow. The flow is
// URL-addressed (?flow=slides|viral|free|ads) so Home can deep-link into it.

export type CreateFlow = StepFlow | "ads";

const FormatCard = styled.button`
  appearance: none;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-4);
  border-radius: var(--radius-xl);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  box-shadow: var(--shadow-xs);
  cursor: pointer;
  text-align: left;
  transition:
    border-color var(--duration-fast) var(--ease-standard),
    transform var(--duration-fast) var(--ease-standard);

  &:hover {
    border-color: var(--color-accent);
    transform: translateY(-1px);
  }
`;

const FormatIcon = styled.span`
  font-size: 26px;
  line-height: 1;
`;

const FormatName = styled.span`
  font-family: var(--font-display);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const FormatDescription = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: 1.5;
`;

const FORMATS: {
  flow: CreateFlow;
  icon: string;
  label: string;
  description: string;
}[] = [
  {
    flow: "slides",
    icon: "🖼",
    label: "Slides",
    description:
      "A carousel built from one of your plan steps. Best for saves and shares.",
  },
  {
    flow: "viral",
    icon: "🎬",
    label: STEP_FLOW_META.viral.label,
    description:
      "An AI-directed reel with your voice narrating a plan step's script.",
  },
  {
    flow: "free",
    icon: "📹",
    label: STEP_FLOW_META.free.label,
    description:
      "A stock-footage reel cut to a plan step's script. No generation cost.",
  },
  {
    flow: "ads",
    icon: "📣",
    label: "Ad",
    description:
      "Paid ad copy and creative for the whole community, not tied to a step.",
  },
];

export type CreateHubProps = {
  flow: CreateFlow | null;
  onSelectFlow: (flow: CreateFlow | null) => void;
};

export const CreateHub: React.FC<CreateHubProps> = ({ flow, onSelectFlow }) => {
  if (flow === "ads") {
    return <AdsStudio onBack={() => onSelectFlow(null)} />;
  }
  if (flow === "slides" || flow === "viral" || flow === "free") {
    return <StepContentStudio flow={flow} onBack={() => onSelectFlow(null)} />;
  }

  return (
    <SectionRoot>
      <div>
        <SectionTitle>What are we making?</SectionTitle>
        <SectionSub>
          Pick a format. Slides and reels are built from a plan step's script.
        </SectionSub>
      </div>
      <CardGrid>
        {FORMATS.map((f) => (
          <FormatCard key={f.flow} type="button" onClick={() => onSelectFlow(f.flow)}>
            <FormatIcon>{f.icon}</FormatIcon>
            <FormatName>{f.label}</FormatName>
            <FormatDescription>{f.description}</FormatDescription>
          </FormatCard>
        ))}
      </CardGrid>
    </SectionRoot>
  );
};

export default CreateHub;
